import * as Sentry from '@sentry/vue';
import { capitalize } from 'lodash';
import { SEVERITY_TYPES } from 'src/constants/externalLogging';
import { remoteConfig } from 'src/store/remoteConfig';

async function shouldEnableReplay(replayConfigKey) {
  if (!replayConfigKey) {
    return false;
  }

  try {
    const remoteConfigStore = remoteConfig();
    return !!await remoteConfigStore.getRemoteConfigValue(replayConfigKey);
  } catch (error) {
    console.error('Failed to resolve replay config value:', error);
    return false;
  }
}

/**
 * Send a notification event to Sentry.
 *
 * @param {Error|string} err - Error object or error message
 * @param {Object} [options] - Optional event options
 * @param {'error'|'warning'|'info'} [options.severity] - Event severity
 * @param {Object} [options.user] - User info
 * @param {Object} [options.context] - Arbitrary additional context/meta
 * @param {Object} [options.tags] - Key-value pairs of Sentry tags, e.g. { paymentFailed: true }
 * @param {String} [options.replayConfigKey] - Remote Config whitelist key used to enable Sentry replay
 * Callers do not need to await logEvent, even when passing a replayConfigKey,
 * to prevent blocking the execution of downstream code.
 * All necessary awaits are managed within logEvent before proceeding further.
 */
// eslint-disable-next-line import/prefer-default-export
export async function logEvent(
  err,
  {
    severity = SEVERITY_TYPES.ERROR,
    user = null,
    context = {},
    tags = {},
    replayConfigKey = null,
  } = {},
) {
  if (!err) {
    return;
  }

  const isEnableReplay = await shouldEnableReplay(replayConfigKey);

  Sentry.withScope((scope) => {
    scope.setLevel(severity);

    if (user) {
      scope.setUser(user);
    }

    scope.setTags({
      ...tags,
      enableReplay: isEnableReplay,
    });

    Object.entries(context).forEach(([section, value]) => {
      const formattedSectionName = section.includes('_')
        ? capitalize(section.replace(/_/g, ' '))
        : section;
      scope.setContext(formattedSectionName, value);
    });

    if (severity === SEVERITY_TYPES.ERROR) {
      Sentry.captureException(err instanceof Error ? err : new Error(err));
    } else {
      Sentry.captureMessage(
        err.message || err.toString(),
        severity,
      );
    }
  });
}
